<template>
    <div class="search-result d-flex flex-column justify-content-start">
        <span v-if="showRegion" class="search-result__region d-flex align-items-end flex-grow-1">
            <RegionIcon :regions="searchResult && searchResult.regions" class="search-result__region__icon" />
            <span class="ml-2">{{ regionNamesLabel }}</span>
        </span>
        <div class="mt-1">
            <VideoPlayer
                class="search-result__video"
                :videoSrc="searchResult && searchResult.video"
                :videoDownloadName="videoDownloadName"
                @loadingVideoFailed="reportError"
            >
                <template slot="missing-video">
                    <MissingSignVideo :sign="searchResult" />
                </template>
            </VideoPlayer>
            <div>
                <router-link
                    v-for="lab in labelTranslations"
                    :key="lab.id"
                    :to="{ name: 'search', query: { lb: lab.id } }"
                    class="color-accent align-items-center sign-detail__body__details__categories__item mb-2"
                >
                    <b-badge pill variant="accent" :class="['label-badge', { 'label-badge--recent': lab.id === 'Recent' }]">
                        {{ lab.name }}
                    </b-badge>
                </router-link>
            </div>
            <div class="search-result__info mt-2 d-flex justify-content-between align-items-start">
                <span class="search-result__info__title" lang="nl">{{ searchResult.translations | join(', ') }}</span>
                <div class="search-result__buttons d-flex align-items-center">
                    <button
                        v-if="showFeedbackButton"
                        class="search-result__feedback-button flex-shrink-0 d-flex align-items-center justify-content-center"
                        @click="selectThisResult"
                        title="Deze is correct"
                    >
                        <font-awesome-icon class="thumbs-up-icon" :icon="['far', 'thumbs-up']" />
                        <span class="feedback-button-text">Deze is correct</span>
                    </button>
                    
                    <router-link
                        v-if="showDetail"
                        class="btn btn-sm search-result__info__button flex-shrink-0 d-flex align-items-center"
                        :to="{
                            name: 'glossdetail',
                            params: { id: searchResult.glossName },
                            query: { sid: searchResult.signId },
                        }"
                    >
                        <font-awesome-icon class="mr-2" :icon="['fal', 'book']" />
                        <span>{{ $t('search.show_detail') }}</span>
                    </router-link>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { Component, Emit, Prop, Vue } from 'vue-property-decorator';
import { ISignOverview } from '../../../../functions/src/shared/contracts/api/GlossContract';
import { signService } from '../../shared/services/SignService';
import { getLastURLSegment } from '../../util/util-functions';
import RegionIcon from '../general/RegionIcon.vue';
import MissingSignVideo from '../video/MissingSignVideo.vue';
import VideoPlayer from '../video/VideoPlayer.vue';
import { namespace } from 'vuex-class';
import { ILabel } from '../../../../functions/src/shared/contracts/api/MasterdataContract';

const cacheModule = namespace('cache');
@Component({ components: { MissingSignVideo, RegionIcon, VideoPlayer } })
export default class SearchResultCard extends Vue {
    public videoDownloadName: string | null = null;

    @Prop({ required: true }) private searchResult!: ISignOverview;
    @Prop({ type: Boolean, default: true }) private showDetail!: boolean;
    @Prop({ type: Boolean, default: true }) private showRegion!: boolean;
    @Prop({ type: Boolean, default: false }) private showFeedbackButton!: boolean;

    @cacheModule.Getter
    private labels!: ILabel[];

    public async created(): Promise<void> {
        this.videoDownloadName = await signService.getSummaryName(this.searchResult);
    }

    @Emit('error')
    public reportError(): ISignOverview {
        return this.searchResult;
    }

    @Emit('feedback-selected')
    public selectThisResult(): ISignOverview {
        return this.searchResult;
    }

    public get videoUrl(): string {
        return getLastURLSegment(this.searchResult.video);
    }

    public get labelTranslations(): ILabel[] {
        return (
            (this.searchResult &&
                this.searchResult.labels &&
                this.labels &&
                this.labels.filter((l) => this.searchResult.labels.includes(l.id))) ||
            []
        ).sort((a: ILabel, b: ILabel) => a.name.localeCompare(b.name));
    }

    public get translationsLabel(): string {
        const arr = (this.searchResult && this.searchResult.translations) || [];
        return Array.isArray(arr) ? arr.join(', ') : '';
    }

    public get regionNamesLabel(): string {
        const ids = (this.searchResult && this.searchResult.regions) || [];
        if (!Array.isArray(ids) || ids.length === 0) return '';
        // Resolve region names from cache (same logic as filter but typed)
        try {
            const regions = (this as any).$store.state.cache.regions || [];
            const names: string[] = ids
                .map((id: string) => {
                    const hit = regions.find((r: any) => r.id === id);
                    return hit ? hit.name : '';
                })
                .filter((n: string) => !!n);
            return names.join(', ');
        } catch (e) {
            return '';
        }
    }
}
</script>

<style lang="scss">
.search-result__info {
    color: $accent-color;
    font-size: 1.125rem;
    font-weight: bold;
    flex-wrap: wrap;
}

.search-result__info__title {
    min-width: 0;
    flex: 1 1 0;
    white-space: normal;
    overflow-wrap: anywhere;
    word-break: break-word;
    hyphens: auto;
    -webkit-hyphens: auto;
    -ms-hyphens: auto;
    word-break: break-word;
}

.search-result__region {
    color: $card-text-color;
}

.search-result__region__icon {
    height: 1.5rem;
    width: 2.5rem;
}

.btn.search-result__info__button {
    background-color: $accent-color;
    color: $accent-text-color;
    height: 31px;
    padding: 0.25rem 0.5rem;
    font-size: 0.875rem;
    order: 2;

    &:hover {
        color: $accent-text-color-hover;
        background-color: $accent-color-hover;
    }
    
    @media (max-width: 575.98px) {
        order: 1;
    }
}

.search-result__buttons {
    gap: 8px;
    
    @media (max-width: 575.98px) {
        flex-direction: column;
        align-items: stretch;
        gap: 8px;
    }
}

.search-result__feedback-button {
    min-width: 31px;
    height: 31px;
    padding: 0 8px;
    flex-shrink: 0;
    order: 1;
    
    border-radius: 3px;
    background: transparent;
    box-shadow: 1px 2px 2px 0 rgba(125, 103, 103, 0.35);
    border: 2px solid #008602;
    color: #008602;
    
    cursor: pointer;
    transition: all 0.2s ease;
    gap: 6px;

    &:hover {
        background: rgba(0, 134, 2, 0.1);
        border-color: #006d02;
        color: #006d02;
    }

    &:focus {
        outline: none;
        box-shadow: 1px 2px 2px 0 rgba(125, 103, 103, 0.35), 0 0 0 2px rgba(0, 134, 2, 0.25);
    }
    
    .thumbs-up-icon {
        width: 16px;
        height: 16px;
        flex-shrink: 0;
        color: #008602;
    }
    
    &:hover .thumbs-up-icon {
        color: #006d02;
    }
    
    .feedback-button-text {
        display: none;
        font-family: Roboto;
        font-size: 14px;
        font-weight: 400;
        white-space: nowrap;
    }
    
    @media (max-width: 575.98px) {
        width: 100%;
        justify-content: center;
        order: 2;
        
        .feedback-button-text {
            display: inline;
        }
    }
}

.label-badge {
    background-color: $accent-color;
    color: $accent-text-color;
    font-weight: normal !important;
    margin-right: 3px;

    &--recent {
        background-color: #4362AD !important;
        color: #ffffff !important;
    }
}
</style>
